import React, { useState } from 'react';
import { useNavigate,useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useGlobalContext } from '../../context/Appcontext';
import {
    Grid,
    Box,
    Button,
    TextField,
    Typography,
    Alert,
    CircularProgress,
    FormLabel,
    FormControlLabel,
    Checkbox,
    IconButton,
    InputAdornment,
    Stack,
} from '@mui/material';
import BannerSection from '../../components/BannerSection';
import logo from '../../assets/login/image 6.png';
import eyeIcon from '../../assets/eye.svg';
import eyeOffIcon from '../../assets/eyeOffIcon.svg';

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login, isAuthenticated } = useGlobalContext();
    const navigate = useNavigate();
    const location = useLocation();

      // Get the previous location (where the user came from)
  const from = location.state?.from?.pathname || '/';

    React.useEffect(() => {
        if (isAuthenticated) {
            // navigate(from, { replace: true });
        }
    }, [isAuthenticated,navigate, from]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(DOMPurify.sanitize(username),DOMPurify.sanitize(password));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const formContainerStyles = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        alignItems: 'center',
        height: '100vh',
        p: { xs: 3, sm: 4, md: 6, lg: 8 },
        maxWidth: { xs: '100%', sm: '600px', md: '100%' },
        margin: '0 auto',
        overflowY: 'auto',
    };

    return (
        <Box sx={{ height: '100vh' }}>
            <Grid container sx={{ overflow: 'hidden' }}>
                {/* Banner Section  */}
                <BannerSection />
                {/* Form Section */}
                <Grid item xs={12} md={6} sx={{ height: '100vh' }}>
                    <Box sx={formContainerStyles}>
                        <Box
                            component="img"
                            src={logo}
                            alt="Optio rx"
                            sx={{
                                maxWidth: '200px',
                                mb: { xs: 10, md: 14 },
                            }}
                        />

                        <Typography
                            component="h1"
                            variant="h4"
                            sx={{
                                fontSize: { xs: '30px', sm: '34px', md: '40px' },
                                fontWeight: 600,
                                mb: 1,
                                textAlign: 'center',
                            }}
                        >
                            Welcome Back!
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 8,
                                textAlign: 'center',
                                color: '#333',
                                fontSize: { xs: '12px', sm: '14px', md: '16px' },
                            }}
                        >
                            Enter your Username and password to access your account
                        </Typography>

                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{
                                width: '100%',
                                maxWidth: '450px',
                                px: { m: 2 },
                                py: { xs: 3, sm: 4 },
                            }}
                        >
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Stack spacing={3}>
                                <Box>
                                    <FormLabel
                                        htmlFor="username"
                                        sx={{ mb: 1, display: 'block' }}
                                    >
                                        Username
                                    </FormLabel>
                                    <TextField
                                        fullWidth
                                        id="username"
                                        placeholder="Enter your username"
                                        name="username"
                                        autoComplete="off"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        disabled={loading}
                                        variant="filled"
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: {
                                                py: 1.5,
                                                px: 2,
                                                '& input': {
                                                    padding: '8px 0',
                                                },
                                            }
                                        }}
                                        sx={{ background: "#F5F7FB", borderRadius: "8px" }}
                                    />
                                </Box>

                                <Box>
                                    <FormLabel
                                        htmlFor="password"
                                        sx={{ mb: 1, display: 'block' }}
                                    >
                                        Password
                                    </FormLabel>
                                    <TextField
                                        fullWidth
                                        name="password"
                                        placeholder="Enter your password"
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        variant="filled"
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: {
                                                py: 1.5,
                                                px: 2,
                                                '& input': {
                                                    padding: '8px 0',
                                                },
                                            },
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        edge="end"
                                                    >
                                                        <img
                                                            src={showPassword ? eyeOffIcon : eyeIcon}
                                                            alt="Toggle password visibility"
                                                        />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ background: "#F5F7FB", borderRadius: "8px" }}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <FormControlLabel
                                        control={<Checkbox />}
                                        label="Remember Me"
                                    />
                                    <Button
                                        variant="text"
                                        sx={{ color: '#E39121', textTransform: "capitalize" }}
                                    >
                                        Forgot Password?
                                    </Button>
                                </Box>

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={loading}
                                    onClick={handleSubmit}
                                    sx={{
                                        mt: 2,
                                        height: 48,
                                        bgcolor: '#E39121',
                                        '&:hover': {
                                            bgcolor: '#d17f1c',
                                        },
                                    }}
                                >
                                    {loading ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        'Sign In'
                                    )}
                                </Button>
                            </Stack>
                        </Box>

                        <Typography
                            variant="body1"
                            sx={{
                                mt: 3,
                                textAlign: 'center',
                            }}
                        >
                            Don't have an Account?{' '}
                            <Box
                                component="span"
                                sx={{ color: '#E39121', cursor: 'pointer' }}
                            >
                                Contact Admin
                            </Box>
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default LoginPage;